import React, { useCallback } from 'react';
import {
  Button,
  ListItemIcon,
  ListItemText,
  MenuItem,
  type ButtonProps,
} from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { useIsElectron } from '@/context/ElectronContext';

interface CheckForUpdatesButtonProps {
  asMenuItem?: boolean;
  onTriggered?: () => void;
  buttonProps?: ButtonProps;
}

const CheckForUpdatesButton: React.FC<CheckForUpdatesButtonProps> = ({
  asMenuItem = false,
  onTriggered,
  buttonProps,
}) => {
  const isElectron = useIsElectron();

  const handleCheckForUpdates = useCallback(() => {
    const api = window.electronAPI;
    if (!api) {
      return;
    }

    if (typeof api.checkForUpdates === 'function') {
      api.checkForUpdates();
    } else {
      api.retryUpdateCheck();
    }

    onTriggered?.();
  }, [onTriggered]);

  if (!isElectron || !window.electronAPI) {
    return null;
  }

  if (asMenuItem) {
    return (
      <MenuItem
        onClick={handleCheckForUpdates}
        aria-label="Check for updates"
        title="Check for updates"
      >
        <ListItemIcon>
          <SystemUpdateAltIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Check for updates" />
      </MenuItem>
    );
  }

  return (
    <Button
      variant="outlined"
      size="small"
      startIcon={<SystemUpdateAltIcon fontSize="small" />}
      onClick={handleCheckForUpdates}
      aria-label="Check for updates"
      sx={{ textTransform: 'none' }}
      {...buttonProps}
    >
      Check for updates
    </Button>
  );
};

export default CheckForUpdatesButton;
